import React, { useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules, getIntlDate, getTestId } from '@utils';
import { useLocation, useNavigate } from '@utils/hooks';

import { globalActions, loginActions, useStore, walletActions } from '@modules';
import { EditUserParams, VerificationStatusesEnum } from '@modules/user/types';

import Switch from '@components/Switch';

import Currency from './Currency';
import Language from './Language';
import ArrowIcon from './arrow.svg';
import Field from '../Field';

import style from './AccountForm.scss';

const { complete, under_review, incomplete } = VerificationStatusesEnum;
const cn = bemCssModules(style, 'AccountForm');
const ti = getTestId('AccountForm');

const arrow = <ArrowIcon className={cn('arrow')} />;

export type TAccountForm = {
  onChangeValue(field: keyof EditUserParams, value: string | boolean): void;
};

const AccountForm: CustomFC<TAccountForm> = ({ onChangeValue, className }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const [{ global, user, wallet }, dispatch] = useStore();
  const userData = user.data;

  const userLanguage = userData?.language_code?.slice(0, 2);
  const countryCode = userData?.country_code;
  const email = userData?.email || '';
  const phone = userData?.phone || '';
  const hasPassword = userData?.has_password;
  const firstName = userData?.first_name_en || userData?.first_name || '';
  const lastName = userData?.last_name_en || userData?.last_name || '';
  const status = userData?.checks.document || incomplete;
  const isAgree = !!userData?.send_marketing_material;
  const strongSecurity = !!userData?.strong_security;
  const birthday = userData?.birthday || '';
  const billingAddress = userData?.billing_address;
  const formattedBirthday =
    birthday &&
    getIntlDate(
      birthday,
      {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      },
      userLanguage
    );

  const streetLine = useMemo(
    () =>
      [
        billingAddress?.street_line_1,
        billingAddress?.street_line_2,
        billingAddress?.zip_code,
        billingAddress?.city,
        !!billingAddress?.state_code &&
          global.states.find(({ code }) => code === billingAddress?.state_code)?.name,
        !!billingAddress?.country_code &&
          global.countries.find(({ code }) => code === billingAddress?.country_code)?.title,
      ]
        .filter(Boolean)
        .join(' '),
    [billingAddress, global.states, global.countries]
  );

  const { phone_mask = '', phone_prefix } =
    global.countries.find(item => item.code === countryCode) || {};
  const phonePlaceholder = `+${phone_prefix} ${phone_mask.replace(/[0-9]/g, '0')}`;

  useEffect(() => {
    if (!phone) {
      dispatch(globalActions.getCountries());
      dispatch(globalActions.getDataByIp());
    }

    if (!wallet.data) dispatch(walletActions.getWallet());
  }, []);

  useEffect(() => {
    if (billingAddress?.country_code)
      dispatch(globalActions.getStates({ country_code: billingAddress.country_code }));
  }, [billingAddress?.country_code]);

  const handleChangeStrongSecurity = (value: boolean) => {
    if (!user.data?.phone) {
      navigate(CONST.nav.profileSetPhone);
    } else if (user.data?.has_password) onChangeValue('strong_security', value);
  };

  const handleChangeAgree = (value: boolean) => {
    onChangeValue('send_marketing_material', value);
  };

  const flowsMap = {
    phone: phone ? CONST.nav.profileChangePhone : CONST.nav.profileSetPhone,
    email: email ? CONST.nav.profileOldEmail : CONST.nav.profileSetEmail,
    password: hasPassword ? CONST.nav.profileChangePassword : CONST.nav.profileSetPassword,
    billing_address: CONST.nav.profileBillingAddress,
  };

  const onClick = (flow: keyof typeof flowsMap) => () => {
    type RedirectToType =
      | 'profileSetPhone'
      | 'profileChangePhone'
      | 'profileSetEmail'
      | 'profileOldEmail'
      | 'profileChangePassword'
      | 'billingAddress';

    const billingAddressFlow =
      flowsMap[flow] === flowsMap.billing_address
        ? {
            redirectTo: CONST.nav.profileEdit,
            from: CONST.nav.profileEdit,
          }
        : {};

    navigate<RedirectToType>(flowsMap[flow], {
      state: {
        strongPassword: strongSecurity,
        ...billingAddressFlow,
      },
    });
  };

  const handleLogout = () => dispatch(loginActions.logout());
  const redirectToKYC = () =>
    navigate<'verifyIdentity'>(CONST.nav.verifyIdentity, { state: { from: location.pathname } });
  const getKYCStatus = () => {
    const isSuccessStatus = status === complete || status === under_review;
    if (isSuccessStatus) return t(`settings.kyc_${status}`);
    return t('settings.kyc_incomplete');
  };

  return (
    <div className={cn(null, [className])} data-testid={ti()}>
      <div className={cn('block')}>
        <Field
          onClick={onClick('phone')}
          label={t('public.phone_label')}
          placeholder={phonePlaceholder}
          value={phone}
          right={arrow}
        />

        <Field
          onClick={onClick('email')}
          label={email ? t('settings.email') : t('account.set_email')}
          value={email}
          placeholder="mercuryo@mercuryo.io"
          right={arrow}
        />

        {firstName && <Field label={t('public.name')} value={firstName} tag="div" />}
        {lastName && <Field label={t('public.last_name')} value={lastName} tag="div" />}
        {formattedBirthday && (
          <Field label={t('settings.birthday')} value={formattedBirthday} tag="div" />
        )}

        <Field
          label={t('common.billing_address_title')}
          onClick={onClick('billing_address')}
          value={streetLine}
          right={arrow}
          placeholder={t('common.provide_address_placeholder')}
        />

        <Field
          value={t('public.verification')}
          right={<div className={cn('status', { status })}>{getKYCStatus()}</div>}
          tag="button"
          onClick={redirectToKYC}
          disabled={status === complete}
        />
      </div>

      <div className={cn('block')}>
        <h2 className={cn('block-title', ['h2'])}>{t('public.security')}</h2>

        <Field onClick={onClick('password')} value={t('account.change_password')} right={arrow} />

        <Field
          value={t('settings.strong_security')}
          tag="label"
          right={
            <Switch
              className={cn('switch')}
              disabled={!user.data?.has_password}
              value={strongSecurity}
              onChange={handleChangeStrongSecurity}
            />
          }
        />

        <p className={cn('sub-text')}>{t('account.high_security_sub_text')}</p>
      </div>

      <div className={cn('block')}>
        <h2 className={cn('block-title', ['h2'])}>{t('public.settings')}</h2>

        <Field
          value={t('account.get_promo')}
          tag="label"
          right={
            <Switch
              className={cn('switch')}
              disabled={!email}
              value={isAgree}
              onChange={handleChangeAgree}
            />
          }
        />

        <p className={cn('sub-text')}>{t('account.promo_subscribe_sub_text')}</p>

        <Currency onChangeValue={onChangeValue} />
        <Language />

        <button className={cn('logout')} onClick={handleLogout} type="button">
          {t('header.log_out')}
        </button>
      </div>
    </div>
  );
};

export default AccountForm;
